import React, { useState } from 'react';
import { Play, X } from 'lucide-react';

interface VideoCardProps {
    id: string;
    title: string;
    thumbnail: string;
    videoUrl: string;
    platform: 'facebook' | 'instagram' | 'youtube' | 'regular';
    isReel?: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({ 
    id, 
    title, 
    thumbnail, 
    videoUrl, 
    platform, 
    isReel = false 
}) => {
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlay = () => {
        setIsPlaying(true);
    };

    const handleClose = () => {
        setIsPlaying(false);
    };

    const getPlatformIcon = () => {
        switch (platform) {
            case 'facebook':
                return (
                    <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full p-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                    </div>
                );
            case 'instagram':
                return (
                    <div className="absolute top-2 right-2 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white rounded-full p-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                    </div>
                );
            case 'youtube':
                return (
                    <div className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div 
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                    isReel ? 'aspect-[9/16]' : 'aspect-video'
                }`}
                onClick={handlePlay}
            >
                <div className="relative w-full h-full">
                    <img 
                        src={thumbnail} 
                        alt={title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                        <div className="bg-white bg-opacity-90 rounded-full p-3 transform transition-transform hover:scale-110">
                            <Play className="w-8 h-8 text-blue-800 ml-1" fill="currentColor" />
                        </div>
                    </div>
                    {getPlatformIcon()}
                    {isReel && (
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs font-medium">
                            Reel
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for video playback */}
            {isPlaying && (
                <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
                    <div className="relative w-full max-w-4xl">
                        <button
                            onClick={handleClose}
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
                        >
                            <X className="w-8 h-8" />
                        </button>
                        <div className={`bg-black rounded-lg overflow-hidden ${isReel ? 'aspect-[9/16] max-w-sm mx-auto' : 'aspect-video'}`}>
                            <iframe
                                src={videoUrl}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={title}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const VideosSection: React.FC = () => {
    const videos: VideoCardProps[] = [
        // Educational Video Talks by Dr. Gangesh Gunjan
        {
            id: 'edu-video-1',
            title: 'Brain Tumors: Myths, Facts & Clinical Realities',
            thumbnail: 'https://img.youtube.com/vi/KzHeJN_zT9E/maxresdefault.jpg',
            videoUrl: 'https://www.youtube.com/embed/KzHeJN_zT9E',
            platform: 'youtube',
            isReel: false
        },
        {
            id: 'edu-video-2',
            title: 'Brain Tumor Symptoms & Clinical Importance of Early Diagnosis',
            thumbnail: 'https://img.youtube.com/vi/Sh1PGomj66c/maxresdefault.jpg',
            videoUrl: 'https://www.youtube.com/embed/Sh1PGomj66c',
            platform: 'youtube',
            isReel: false
        },
        {
            id: 'edu-video-3',
            title: 'Pediatric Neurological & Developmental Health Awareness',
            thumbnail: 'https://img.youtube.com/vi/deRh99cBNXY/maxresdefault.jpg',
            videoUrl: 'https://www.youtube.com/embed/deRh99cBNXY',
            platform: 'youtube',
            isReel: false
        }
    ];

    return (
        <section id="videos" className="py-16 px-4 lg:px-8 bg-gradient-to-br from-indigo-50 to-indigo-100">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                        Neurosurgical Health Education
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
                        Evidence-based medical education on brain conditions, tumor symptoms, and neurosurgical awareness by Dr. Gangesh Gunjan
                    </p>
                    <p className="text-sm text-gray-500 italic">
                        Clinical Education Initiative • Sarvodaya Hospital, Greater Noida West
                    </p>
                </div>

                <div className="space-y-12">
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {videos.map((video, index) => (
                                <div key={video.id} className="bg-white rounded-3xl shadow-xl p-6 flex flex-col justify-between">
                                    <div className="space-y-4">
                                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Medical Awareness Part {index + 1}
                                        </div>
                                        <h4 className="text-lg font-bold text-gray-900 min-h-[56px] leading-snug">
                                            {video.title}
                                        </h4>
                                        <div className="flex items-center space-x-2 text-xs text-gray-500 mb-2">
                                            <span className="flex items-center text-red-600 font-medium">
                                                <svg className="w-4 h-4 mr-1 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                </svg>
                                                Sarvodaya Healthcare
                                            </span>
                                            <span>•</span>
                                            <span>Clinical Talk</span>
                                        </div>
                                        <VideoCard {...video} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default VideosSection;
